import { type ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { Adg4Button } from "@/components/adg4/button/src";
import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import TryIntent from "@/components/try-intent/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row, TypographyStyles } from "@/libraries/wpl/foundations/src";

import HeaderTextBlock from "./header-text-block";

const sectionStyle = css({
    paddingBlockEnd: token("space.200"),
    overflow: "hidden"
});

const backgroundBlue = css({
    position: "absolute",
    insetBlockStart: 0,
    minWidth: 1440,
    width: "100vw",
    height: "100%",
    background: "#E9F2FE",
    clipPath: "polygon(0 0, 100% 0, 100% 90%, 0 100%)",
    zIndex: -1
});

const relative = css({
    position: "relative",
    display: "flex",
    justifyContent: "center"
});

const HelloTeamworkHeader = () => {
    return (
        <FormattedMessage
            id="ConfluenceSoftwarePage.v4.heroHeader"
            defaultMessage="Goodbye silos, <bold-text>hello</bold-text> <underline-comp>teamwork</underline-comp>"
            values={{
                "bold-text": (node: ReactNode) => (
                    <span className="superhero">{node}</span>
                ),
                "underline-comp": (node: ReactNode) => (
                    <Adg4TextUnderline
                        underlineInset="-28%"
                        underline={
                            <svg
                                width="100%"
                                height="20px"
                                viewBox="0 0 335 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M2 9V8l-.3.9-.4-.2a1 1 0 0 1 0-1.5l.3-.2.8-.2 13.8-.4 7-.2 5-.2 5.3-.1 8-.3 5.4-.1 2.4-.1 2.4-.1 6-.2 10.6-.3L97.7 4l7.3-.2 21-.6h3.6l4-.2 37.8-.7 14.4-.3zm0 0 14-.4 9.3-.3 4.2-.2 7.1-.2h2.6l6-.2 4.2-.2h3.3L62.9 7l21.2-.6 2.3-.1 13.7-.4 5.5-.2h4.1M2.1 8.8 287 3.4m-187 .7v.8l9.7-.3 5-.1 2-.1v-.8m-16.7.5 5.6-.2 4-.1h1.8l5.3-.2m-16.7.5-6.2.1-7.4.2zm16.7-.5v.8l9.4-.3h7.5l13.6-.4v-.8m-30.5.7h1.7l7.6-.2 5.9-.2h1.7l13.6-.3m0 0v.8l23.8-.5v-.8zm23.8-.5v.8h.4l14.4-.2M171 2.4l71.8-1v.2m-71.8.8h.7l14-.2m-14.7.2 14.8-.2m0 .8 4.6-.1v-.8m-4.6.9v-.8h4.6m-4.6.8v-.8m4.6 0v.7h5.9m-6-.8h6m0 .7h.4l6.8-.1h8.7m-16 .1V2m0 .8V2m16 .6h11.3l9.6-.1m-20.9.1v-.8l20.9-.1m-20.9 1v-.9l20.9-.1m0 .8h9.7m-9.7 0v-.8m0 .8v-.8m9.7.7h6.7v-.8m-6.7.8v-.8m0 .8v-.8m6.7 0v.8H268v-.8zm0 0h-6.7m30-.2v.2m0-.2L323 2m-50.4-.5H287l6.8.1h.3zm0 0v.2m0-.2 15.2.2m-15.2 0H268v.8h4.7m0-.8v.8m0 0H288m-15.3 0v-.8m15.2.8h5.9l.3.1m-6.2 0v-.9m0 .8v-.8m6.2.9h2m-2 0v-.9zm2 0h7v-.8m-7 .8v-.8m0 .8v-.8m7 0v.8h3.3m-3.2-.8h3.2m-3.2 0h-7m10.2.9h4.9v-.8m-4.9.8v-.9m0 .9v-.9m5 .1v.8h2.3m-2.4-.8h-4.8m4.8 0-15.1-.1m17.5 1h6v-.9m-6 .9v-.9m0 .9v-.9m6 0v1h3.4v-1m-3.4 0h3.4m-3.4 0h-6m9.4 0-9.4-.1v.1m9.4 0v1zm4.5 1h-4.3l9-.8m-4.7.8h2.4m-2.4 0v-1m0 1v-1m2.4 1h1.5V2v.8V2m-1.5.8V2m0 1V2m1.6 0H330m1.6 0v.7l.3-.3V2m-.3 0v-.1m0 0h.2m0 0h.2m-.2 0h.1m.1 0v.3h.2l.2-.1h.2M332 2h.4m.2 0v.1m0 0v-.2h-.6m.6.1h.4m-.4 0h.3m0 0v.1m0 0h.1m0 0h-.1v.1m0 0h.2m-.2 0h-.2v.3m.2-.3v.1l-.1.2m0 0v.2-.1m0 0-.1-.2v-.2m0 .4v.3m0-.7v.5m0-.5h-.1m0 0v.4l-.1.3m0-.8.1.5m-.1.3V2v.1h-.1l-.4.7h.5Zm0 0h.1m0 0v-.2m0 .2v-.2m.5-.5v.1l.1.2-.2.2.3-.1v.3l.6-.1-.4-.6-.4-.2v.2m-1.2-.3h-.3m0 0H330m0 0h-2.4m0 0h-.5l-.6-.1h-3.2 4.2m0 0 .5.1h1.9m0 0h.1m2.4.1Zm.8.2Zm-.6 1.7-.2-.1.1-.5m0 .5v-.5m0 0v-.1l.1-.1zm0-.3Zm0 0h-.5v.3l.2.2.2-.3V3v.1V3zm-.4.8h-.2m.2 0 .3.1m-.3 0h.3m-.3 0h.3m-.6 0h.6m-.6 0h-.1m.1 0h-.1m0 0v-.3l-.1-.1-.1-.2v-.4h-.4m.6 1-.2-.1m-.4-.9h-1.5m1.5 0v.9m0-.9v.9m-1.5-1h-2m2 0v1m0-1v1m-2-1h-1.1v1h1m0-1v1m0-1v1m0 0h2.1m0 0h1.4m0 0h.1m0 0h.4m.9.2h.2v-.1m-.2 0h.2m-.2 0Zm-.8-.1Zm-16.4-.2v.2l-47.6-1.3m47.6 1.1 4.5.1h.6m-5 0h-.1m0 0v-1m5.1 1h6.1l.1-.8h-6.2zm0 0v-.9h-5m0 0h-6.6v.7m6.5-.7v.8m-6.5 0v-.9h-4.3v.8m4.3 0h3.2l1.8.1h1.5m-6.5 0h-4.3m0 0v-1H287v.8m17.4.1h-17.5m0 0v-1h-19.3m0 0h-19.1v.9h19.1zm-19 .9v-.9h-5.8l-30.6.3-8.7.1m45 .5L51.8 7.2v-.7h.8l15.8-.6 7.2-.2m173-2.3H233l-20.9.2h-8.7m45-.2-45 .3m0-.8h-4.7l-2.1.1m6.8 0v.7m0-.8v.8m-6.8-.7h-3.9l-21 .4h-.8l-29.8.6-7.6.1V5m63-2v.8m0-.8v.8m-63 1.2V4H132m1.7.9 13.6-.4 24.3-.4 14.3-.3h4.6l5.9-.1h.4m-63 1.2H132m0-.8-5.8.2V5m5.8-1v1m0-1v1m-5.8 0v-.7l-16.4.4m16.4.3h5.8m-5.8 0-9.4.3h-2l-5 .2m0-.8H105l-5 .2-6.1.2-7.5.2H84l-8.4.3m34.1-1v.9m0-.8v.8m-34 .1v.8m0-.8v.8m34-.8v-.1m0 .1v-.1m0 .1h1.7l5.3-.2h1.7l13.5-.3m-22.2.4H105l-5 .2h-2.2l-11.4.4-10.8.3M132 5.2l9.3-.2 6-.1 23.8-.5h.8l18.6-.3h2.5l3.8-.1M132 5.2V5m0 .2V5m0 .2V5m0 .2V5m64.8-1h2.1l4.7-.1m-6.8 0v-.1m0 .2v-.2m6.8.1q14.8-.3 29.6-.3h16.5l5.2-.1h41.3l7 .2h12.1m-111.7.2v-.2h-6.8m6.8.2-128 2.6M203.5 4v-.2m0 .2v-.2m84.4-2h-1l-14.2-.1m-76.4.4-10.5.2m147 1.8Zm0-.1Zm0-.2-.2-.4V3v.2zm-.3-.7ZM2.5 8.8Zm330-5.8h.1Zm.2 0 .1-.1zm.1-.2ZM212.2 1.7a734 734 0 0 0-26.4.3z"
                                    fill="#1868DB"
                                    stroke="#1868DB"
                                    strokeWidth="1.9"
                                />
                            </svg>
                        }
                    >
                        {node}
                    </Adg4TextUnderline>
                )
            }}
        />
    );
};

const HeroTextSection = () => {
    const intl = useIntl();
    const ctaText = intl.formatMessage({
        id: "ConfluenceSoftwarePage.v4.heroCTA",
        defaultMessage: "Get Confluence free"
    });

    const signupLink = useLocalizedHref("/software/confluence/free");
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.CONFLUENCE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    return (
        <UFOSegment name="confluence-pt-cfind-1234-heroTextSection">
            <TypographyStyles>
                <section css={sectionStyle}>
                    <Row>
                        <div css={relative}>
                            <div css={backgroundBlue} />
                            <HeaderTextBlock
                                header={<HelloTeamworkHeader />}
                                subHeader={
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePage.v4.heroSubHeading"
                                        defaultMessage="Create, share, and harness knowledge across teams"
                                    />
                                }
                                cta={
                                    <TryIntent
                                        intentType={"button"}
                                        containerId={"heroSection"}
                                        productKey={"confluence"}
                                    >
                                        <Adg4Button
                                            key={"confluence"}
                                            variant="primary"
                                            size="large"
                                            color="blue"
                                            onClick={openCrossFlow}
                                            href={signupLink}
                                            dataEventProps={{
                                                "data-product-key":
                                                    "confluence",
                                                "data-event": "clicked",
                                                "data-event-component":
                                                    "linkButton",
                                                "data-event-container":
                                                    "heroSection",
                                                "data-label":
                                                    "hero-get-confluence-free",
                                                "data-label-english":
                                                    "Get Confluence free",
                                                "data-track-event":
                                                    "confluence-pt.get-confluence-free.cta"
                                            }}
                                        >
                                            {ctaText}
                                        </Adg4Button>
                                    </TryIntent>
                                }
                            />
                        </div>
                    </Row>
                </section>
            </TypographyStyles>
        </UFOSegment>
    );
};

HeroTextSection.displayName = "HeroTextSection";

export default HeroTextSection;
